import {
  useEffect, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Stack,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { Masonry } from '@mui/lab';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import { getEventTimeLine } from '../../features/EventTimeline/EventTimelineAction';
import { CustomIcon } from '../../components/CustomIcon';
import { ACCOUNT_CARDS_ID } from '../../utils/constants';
import { ConfirmDialogBox } from '../../components/CustomDialogBox';
import palette from '../../theme/palette';
import { MutedText } from '../../components/CustomTypography';
import useStyles from '../../hooks/useStyles';
import { PackageHeader } from '../../components/CustomHeading';
import {
  getCustomObjects,
  getCustomObjectPinningData,
  getGeneralDetails,
  // getAllRemarks,
  getSiteConfiguration,
  getPackageList,
  getAccountLicenses,
} from '../../features';
import {
  KpiScoreOverview,
} from '../../modules/Accounts';
import useAccountDetail from './useAccountDetail';
import useToggle from '../../hooks/useToggle';
// import { updateAccountVisibility } from '../../features/AccountList/accountListAction';
import styles from '../../layouts/pages/style';
import { openSnackbar } from '../../features/SnackBar/snackBarSlice';
// import { resetAccountsList } from '../../features/AccountList/accountListSlice';
import { PersonaDropdown } from '../../modules/Dashboard';
import usePersona from '../../modules/Dashboard/components/usePersona';
// import { EventTimeLine } from '../../modules/Accounts/components/EventTimeline';

function AccountDetail(props) {
  const {
    isShareableScreens, permissions, userInfo, tenantId, accountOrgId,
  } = props;

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const accountId = searchParams.get('accountId');
  const accountState = location.state;
  const [isShare, setIsShare] = useState(isShareableScreens);
  const [accountName, setAccountName] = useState('');
  const [refreshUsers, setRefreshUsers] = useToggle();
  const [openDialog, setOpenDialog] = useState();
  const urlRef = useRef(null);
  const { personaList, selectedPersona, handlePersonaChange } = usePersona();
  const siteConfiguration = useSelector((state) => state.siteConfiguration?.data);
  const [trendData, setTrendData] = useState({ healthScoreTrend: '', trendValue: '' });

  useEffect(() => {
    dispatch(getPackageList());
  }, []);

  useEffect(() => {
    setIsShare(isShareableScreens);
  }, [isShareableScreens]);

  useEffect(() => {
    const salesforceParams = { isShare, accountOrgId, tenantId };
    dispatch(getSiteConfiguration(salesforceParams));
    dispatch(getCustomObjectPinningData(salesforceParams));
    // dispatch(getAllRemarks({ accountId, ...salesforceParams }));
    // dispatch(getEventTimeLine({ accountId, ...salesforceParams }));
    dispatch(getGeneralDetails({ accountId, ...salesforceParams }));
    dispatch(getCustomObjects({ view: '30days', accountId, ...salesforceParams }));
    dispatch(getAccountLicenses({ accountId, ...salesforceParams }));
  }, [accountId, isShare, accountOrgId, tenantId]);

  const {
    generalDetail,
    // visibility, setVisibility,
    visibleBoxIds, boxComponents,
  } = useAccountDetail({
    selectedPersona,
    refreshUsers,
    setRefreshUsers,
    permissions,
    isShareableScreens,
    tenantId,
    accountId,
    accountName,
    accountOrgId,
  });

  useEffect(() => {
    if (!generalDetail.loading) {
      setAccountName(accountState?.accountName || generalDetail?.data?.accountName || accountId);
      const data = generalDetail?.data;
      const trendInfo = { healthScoreTrend: (siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (data?.weeklyTrend) : data?.healthScoreTrend), trendValue: (siteConfiguration?.scoreTrendTimeframe === 'weekly' ? (data?.weeklyTrendValue) : data?.trendValue) };
      setTrendData(trendInfo);
    }
  }, [accountState?.accountName, generalDetail?.data, !generalDetail.loading]);

  // const handleVisibility = () => {
  //   dispatch(updateAccountVisibility({ accountId, visibility: !visibility })).then((res) => {
  //     if (res?.payload?.status === 'SUCCESS') {
  //       setVisibility(!visibility);
  //       dispatch(resetAccountsList());
  //     }
  //     return null;
  //   });
  // };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCopyToClipboard = () => {
    // Select the text in the input field
    const input = urlRef.current.querySelector('input');
    if (input) {
      input.select();
      document.execCommand('copy');
      // Deselect the text
      window.getSelection().removeAllRanges();
      dispatch(
        openSnackbar({
          open: true,
          message: 'Public URL copied to clipboard',
          type: 'success',
        }),
      );
      setOpenDialog(false);
    }
  };

  const navigate = useNavigate();
  const classes = useStyles(styles)();

  const handleNavigateBack = () => {
    const state = { filters: accountState?.selectedFilters, isNavigatedFromAccountDashboard: true };
    navigate(`/account-list/${accountState?.accountListMenuId}`, { state });
  };

  return (
    <Grid container gap={2}>
      <Grid item xs={12} className={classes.cardStyles}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Box>
            <Stack flexDirection="row" gap={2} alignItems="center">
              <CustomIcon
                size="small"
                variant="contained"
                onClick={handleNavigateBack}
                Icon={ArrowBackIcon}
              />
              <PackageHeader trendData={trendData} heading={accountName} noPackage />
              <PersonaDropdown
                personaList={personaList}
                selectedPersona={selectedPersona}
                handlePersonaChange={handlePersonaChange}
              />
            </Stack>
            <MutedText text={accountState?.packageLabel} />
          </Box>

          {!isShareableScreens && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <ConfirmDialogBox
              title="Public URL"
              message={(
                <TextField
                  ref={urlRef}
                  value={`${window.location.href}&isShare=true&tenantId=${userInfo.tenantId}`}
                  readOnly
                  style={{
                    width: '100%',
                  }}
                />
                     )}
              openDialog={openDialog}
              onClose={handleCloseDialog}
              onConfirm={handleCopyToClipboard}
              confirmText="Copy"
            />
            <Tooltip title="Copy public page link">
              <IconButton
                color="primary"
                variant="outlined"
                onClick={handleOpenDialog}
                style={{ color: palette.primary.mainColor }}
              >
                <ShareIcon />
              </IconButton>
            </Tooltip>

            {/* {permissions.write && (
            <Button variant="outlined" onClick={handleVisibility}>
              {visibility ? 'Exclude From Reports' : 'Include In Reports'}
            </Button>
            )} */}
          </Stack>
          )}
        </Stack>
      </Grid>

      {visibleBoxIds.includes(ACCOUNT_CARDS_ID.ACCOUNT_STATS) && (
      <Grid item xs={12}>
        <KpiScoreOverview
          accountId={accountId}
          generalDetail={generalDetail}
          permissions={permissions}
          tenantId={tenantId}
          accountOrgId={accountOrgId}
          isShare={isShareableScreens}
        />
      </Grid>
      )}

      <Masonry columns={{ xs: 1, sm: 2 }}>
        {visibleBoxIds.map((boxId) => boxComponents[boxId] && <Box key={boxId}>{boxComponents[boxId]}</Box>)}
      </Masonry>

      {/* {visibleBoxIds.includes(ACCOUNT_CARDS_ID.EVENT_TIMELINE) && (
      <Grid item xs={12} mt={-2}>
        <EventTimeLine />
      </Grid>
      )} */}
    </Grid>
  );
}

export default AccountDetail;
